/**
 * Alkahest text generation provider (OpenAI-compatible)
 */

import { BaseProvider } from '../../core/base-provider';
import {
  TextGenerationOptions,
  TextResponse,
  UsageStats,
  ProviderError,
  Message,
  RateLimitError,
} from '../../core/types';

export interface AlkahestTextProvider extends BaseProvider {
  generate(prompt: string, options?: TextGenerationOptions): Promise<TextResponse>;
  generateWithMessages(messages: Message[], options?: TextGenerationOptions): Promise<TextResponse>;
  stream(prompt: string, options?: TextGenerationOptions): AsyncGenerator<string>;
  streamWithMessages(messages: Message[], options?: TextGenerationOptions): AsyncGenerator<string>;
}

export class AlkahestText extends BaseProvider implements AlkahestTextProvider {
  private baseUrl: string = '';
  private apiKey?: string;

  constructor() {
    super('alkahest-text', 'Alkahest Text', 'text');
  }

  protected async onInitialize(): Promise<void> {
    this.baseUrl = this.config.baseUrl || 'http://localhost:9999';
    this.apiKey = this.config.apiKey;

    // Alkahest supports both API key and Privy token auth
    if (!this.apiKey && !this.config.privyToken) {
      throw new ProviderError('Alkahest API key or Privy token is required', this.id);
    }
  }

  protected async checkAvailability(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }

  protected async fetchUsage(): Promise<UsageStats> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/mana/balance`, {
        headers: this.getHeaders(),
      });

      if (!response.ok) return { tokensUsed: 0, requestsCount: 0, cost: 0 };

      const data = await response.json();
      return {
        remaining: (data as any).balance,
        tokensUsed: 0,
        requestsCount: 0,
        cost: 0,
      };
    } catch {
      return { tokensUsed: 0, requestsCount: 0, cost: 0 };
    }
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    } else if (this.config.privyToken) {
      headers['Authorization'] = `Bearer ${this.config.privyToken}`;
    }

    return headers;
  }

  async generate(prompt: string, options?: TextGenerationOptions): Promise<TextResponse> {
    const messages: Message[] = [];
    if (options?.systemPrompt) {
      messages.push({ role: 'system', content: options.systemPrompt });
    }
    messages.push({ role: 'user', content: prompt });

    return this.generateWithMessages(messages, options);
  }

  async generateWithMessages(
    messages: Message[],
    options?: TextGenerationOptions
  ): Promise<TextResponse> {
    this.ensureInitialized();

    try {
      const response = await fetch(`${this.baseUrl}/api/v1/chat/completions`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          model: options?.model || 'gpt-4o',
          messages: messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          stream: false,
          temperature: options?.temperature,
          max_tokens: options?.maxTokens,
          top_p: options?.topP,
          frequency_penalty: options?.frequencyPenalty,
          presence_penalty: options?.presencePenalty,
          stop: options?.stop,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));

        if (response.status === 429) {
          const retryAfter = response.headers.get('X-RateLimit-Reset');
          throw new RateLimitError(
            'Rate limit exceeded',
            this.id,
            retryAfter ? parseInt(retryAfter) : undefined
          );
        }

        if (response.status === 402) {
          throw new ProviderError('Insufficient mana balance', this.id, error);
        }

        throw new ProviderError(
          (error as any).error?.message || (error as any).error || 'Failed to generate text',
          this.id,
          error
        );
      }

      const data = await response.json();
      const choice = (data as any).choices[0];

      return {
        text: choice.message.content || '',
        model: (data as any).model,
        usage: (data as any).usage
          ? {
              promptTokens: (data as any).usage.prompt_tokens,
              completionTokens: (data as any).usage.completion_tokens,
              totalTokens: (data as any).usage.total_tokens,
            }
          : undefined,
        finishReason: choice.finish_reason || undefined,
      };
    } catch (error: any) {
      if (error instanceof ProviderError || error instanceof RateLimitError) {
        throw error;
      }

      throw new ProviderError(error.message || 'Failed to generate text', this.id, error);
    }
  }

  async *stream(prompt: string, options?: TextGenerationOptions): AsyncGenerator<string> {
    const messages: Message[] = [];
    if (options?.systemPrompt) {
      messages.push({ role: 'system', content: options.systemPrompt });
    }
    messages.push({ role: 'user', content: prompt });

    yield* this.streamWithMessages(messages, options);
  }

  async *streamWithMessages(
    messages: Message[],
    options?: TextGenerationOptions
  ): AsyncGenerator<string> {
    this.ensureInitialized();

    try {
      const response = await fetch(`${this.baseUrl}/api/v1/chat/completions`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          model: options?.model || 'gpt-4o',
          messages: messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          stream: true,
          temperature: options?.temperature,
          max_tokens: options?.maxTokens,
          top_p: options?.topP,
          frequency_penalty: options?.frequencyPenalty,
          presence_penalty: options?.presencePenalty,
          stop: options?.stop,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new ProviderError(
          (error as any).error?.message || (error as any).error || 'Failed to stream text',
          this.id,
          error
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new ProviderError('No response body', this.id);
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last line in buffer if it's incomplete
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') return;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices[0]?.delta?.content;
              if (content) {
                yield content;
              }
            } catch (_e) {
              // Skip parse errors
            }
          }
        }
      }
    } catch (error: any) {
      if (error instanceof ProviderError) {
        throw error;
      }

      throw new ProviderError(error.message || 'Failed to stream text', this.id, error);
    }
  }
}
